import { SUPPORTED_EXCHANGES } from "../../constants/supported-cex";
import { tokenPairSymbolSchema } from "../../orderbook";
import {
  BattleRoyaleConfigReadSchema,
  isRisingDrawdown,
  isRisingDrawdownIntervalWithinDuration,
  PaperTradingConfigBaseSchema,
  RISING_DRAWDOWN_INTERVAL_EXCEEDS_DURATION_MESSAGE,
} from "../paper-trading/paper-trading-config";
import {
  assertBattleRoyaleInvariants,
  assertCedeFillPrizePoolHasPositivePool,
  assertSpinAndGoInvariants,
  SaveCampaignDtoFieldsSchema,
  TradeToEarnCampaignPreviewDtoSchema,
} from "../trade-to-earn/admin-trade-to-earn-campaign.dto";
import { CampaignMarketType } from "../trade-to-earn/trade-to-earn-campaign.dto";
import {
  assertSlotsUsdtNonIncreasing,
  EMPTY_PRIZE_SLOT_MESSAGE,
  freePrizeSlotsIssueMessage,
  freePrizeSlotsIssues,
  isEmptyPnlRewardSlot,
  PAID_COMPETITION_NEEDS_USDT_PRIZE_MESSAGE,
  parseFreePrizePool,
  PnlRuleCmsSettingsWithCompetitionChecksSchema,
  SPIN_AND_GO_USDT_ONLY_PRIZES_MESSAGE,
} from "../trade-to-earn/trade-to-earn-rules";
import { BATTLE_ROYALE_MAX_PLAYERS } from "./battle-royale";
import { ArenaRakeBpsSchema } from "./rake";
import Decimal from "decimal.js";
import { z } from "zod";

/**
 * Cede Arena Competition Blueprints
 * =================================
 * A blueprint defines a *recurring* family of single-window Cede Arena
 * competitions. It is persisted once and a rolling cron materializes the
 * concrete `TradeToEarnCampaign` rows (one per occurrence) by cloning the
 * embedded `template` and stamping the per-occurrence name + dates.
 */

/**
 * How far ahead (in days) competitions are materialized from a blueprint. The
 * generation worker keeps topping this window up — competitions starting beyond
 * it are created later, as their start date approaches. Shared by the API
 * generator and the CMS validity-window warning so they never drift.
 */
export const CEDE_ARENA_BLUEPRINT_GENERATION_HORIZON_DAYS = 30;

const ENTRY_FEES_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/;

/** Free competition: direct opt-in at a zero entry fee. A null `entryFees` has no opt-in fee concept at all. */
export const isFreeArenaCompetition = (competition: {
  userCanOptIn?: boolean | null;
  entryFees?: Decimal.Value | null;
}): boolean =>
  competition.userCanOptIn === true &&
  competition.entryFees != null &&
  // A malformed fee string is reported by the schema; it must not throw out of a refinement.
  (typeof competition.entryFees !== "string" || ENTRY_FEES_PATTERN.test(competition.entryFees)) &&
  new Decimal(competition.entryFees).isZero();

/** Same opt-in fee amount ("10" = "10.00"); a missing fee (null or undefined) only equals another missing one. */
export const sameArenaEntryFees = (current: string | null | undefined, next: string | null | undefined): boolean => {
  if (current == null || next == null) return (current == null) === (next == null);
  return new Decimal(current).eq(next);
};

export const RecurrenceWeekdaySchema = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);
export type RecurrenceWeekday = z.infer<typeof RecurrenceWeekdaySchema>;

/**
 * Weekday filtering for a recurrence. Either an exclusion list (`not`) or an
 * inclusion list (`only`) — never both. Absent means "every day the base rule
 * fires".
 */
export const RecurrenceRuleSpecsSchema = z.union([
  z.object({ not: z.array(RecurrenceWeekdaySchema).min(1) }).strict(),
  z.object({ only: z.array(RecurrenceWeekdaySchema).min(1) }).strict(),
]);
export type RecurrenceRuleSpecs = z.infer<typeof RecurrenceRuleSpecsSchema>;

export const RecurrenceRuleNameSchema = z.enum(["every_day", "every_week", "every_month"]);
export type RecurrenceRuleName = z.infer<typeof RecurrenceRuleNameSchema>;

export const RecurrenceRuleSchema = z.object({
  ruleName: RecurrenceRuleNameSchema,
  specs: RecurrenceRuleSpecsSchema.optional(),
  /** Start-of-day-relative time of the occurrence, 24h UTC, "HH:mm". */
  timeUtc: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "timeUtc must be HH:mm (24h, UTC)"),
});
export type RecurrenceRule = z.infer<typeof RecurrenceRuleSchema>;

/**
 * A free competition pays fixed, Cede-funded prizes: its slots are literal USDT amounts in whole
 * cents adding up exactly to `prizePool`, and nothing that resizes or skims a pot applies. Spin &
 * Go and priority tickets are already refused on a zero fee by their own checks.
 */
function assertFreeCompetitionInvariants(
  template: {
    prizePool: string;
    rakeBps?: number;
    cedeFillPrizePool?: boolean;
    rule: { data: { slots: ReadonlyArray<{ quantity: number; reward: Decimal }> } };
  },
  ctx: z.RefinementCtx,
): void {
  const reject = (path: (string | number)[], message: string) => ctx.addIssue({ code: "custom", path, message });
  const slotsPath = ["rule", "data", "slots"];
  if (template.rakeBps != null && template.rakeBps > 0) reject(["rakeBps"], "A free competition takes no rake");
  if (template.cedeFillPrizePool) reject(["cedeFillPrizePool"], "A free competition's prize pool is already fixed");
  const prizePool = parseFreePrizePool(template.prizePool);
  if (prizePool == null) {
    reject(["prizePool"], "A free competition needs a prize pool (0 when it only pays tickets)");
    return;
  }
  for (const issue of freePrizeSlotsIssues(template.rule.data.slots, prizePool)) {
    reject(issue.kind === "pool_not_whole_cents" ? ["prizePool"] : slotsPath, freePrizeSlotsIssueMessage(issue));
  }
}

/**
 * The minimal campaign config a blueprint clones for every occurrence. Only the
 * fields an admin actually chooses per blueprint live here — everything else a
 * Cede Arena competition needs is fixed by the generator (PUBLISHED, public,
 * manual trading, no partners/UTMs/authorized list, all socials required,
 * Hyperliquid referee check disabled, shared reward vault, …) or comes from the
 * blueprint's own window/exchanges/pairs. The single per-occurrence epoch group
 * is built from `rule`, so the reward rule lives here, not in `epochGroups`.
 */
export const CedeArenaCompetitionBlueprintTemplateSchema = SaveCampaignDtoFieldsSchema.pick({
  marketType: true,
  prizePool: true,
})
  .extend({
    /**
     * Reward rule applied to the single epoch group of every generated competition.
     * Cede Arena competitions are always scored on PnL. Competition mode (which
     * every arena blueprint uses) additionally requires a positive initial
     * deposit + graph display options — see the refinement.
     */
    rule: PnlRuleCmsSettingsWithCompetitionChecksSchema,
    /**
     * Whether every generated competition lets users opt in directly from the
     * Arena page (locking `entryFees` from their virtual balance). Pre-checked
     * (true) in the CMS blueprint form; absent on legacy templates, treated as
     * false by the generator so opt-in is never silently enabled.
     */
    userCanOptIn: z.boolean().optional(),
    /**
     * Per-competition entry fee, as a decimal-USDT string (e.g. "10"). Locked on
     * opt-in and consumed at competition start. Required when `userCanOptIn` is
     * true; "0" makes it a free competition ({@link isFreeArenaCompetition}).
     */
    entryFees: z
      .string()
      .regex(ENTRY_FEES_PATTERN, {
        error: 'entryFees must be a USDT amount with up to 6 decimals, e.g. "10" or "10.5"',
      })
      .optional()
      .nullable(),
    /**
     * Maximum number of users that may opt into each generated competition. The
     * opt-in command rejects further opt-ins once this many users have joined,
     * and the Arena page locks the opt-in button. Only meaningful when
     * `userCanOptIn` is true; absent/null means unlimited (a Battle Royale: up to BATTLE_ROYALE_MAX_PLAYERS).
     */
    maxParticipants: z.number().int().positive().optional().nullable(),
    /**
     * When true, `prizePool` is a floor: each generated competition's pool at start
     * is max(prizePool, participants × entryFees). Only meaningful when
     * `userCanOptIn` is true; defaults to false (self-funded pool).
     */
    cedeFillPrizePool: z.boolean().optional(),
    /**
     * When true, each generated competition's prize pool is resized by a drawn
     * Spin & Go pot multiplier (`potFactor × collected buy-ins`). Requires a
     * positive `entryFees` and is mutually exclusive with `cedeFillPrizePool`.
     * Only meaningful when `userCanOptIn` is true; defaults to false.
     */
    isSpinAndGo: z.boolean().optional(),
    /**
     * Paper-trading fee override (bps) applied to every generated competition's
     * `paperTradingConfig`. Omitted/null fields fall back to the module defaults
     * (PAPER_MAKER_FEE_BPS/PAPER_TAKER_FEE_BPS) in `resolveCampaignConfig`,
     * currently 0 — so blank and 0 are both fee-free; set a positive value to
     * charge a fee. Only meaningful for competitions that trade on the paper exchange.
     * `drawdown`: optional drawdown-elimination policy cloned onto every
     * generated competition; omitted/null ⇒ off. `battleRoyale`: the elimination
     * schedule that makes every occurrence a Battle Royale; omitted/null ⇒ classic.
     * `priceModel`: how every generated competition's paper orders fill.
     */
    ...PaperTradingConfigBaseSchema.pick({
      makerFeeBps: true,
      takerFeeBps: true,
      drawdown: true,
      battleRoyale: true,
      priceModel: true,
    }).shape,
    /**
     * Platform rake in basis points (0–5000, 0.25% steps) taken off each
     * generated competition's prize pool before the winner is paid. Snapshotted
     * onto every occurrence at creation and locked for its lifetime. Absent on
     * legacy blueprints ⇒ treated as 0 (no rake) by the generator.
     */
    rakeBps: ArenaRakeBpsSchema.optional(),
    /**
     * Autopilot priority tickets granted, when a generated competition
     * finalizes, to each recipient that played a positive entry fee — bots and
     * waived (whitelisted) entries earn nothing. Who qualifies is
     * `cede_arena:api:priority_tickets_all_participants_enable`: on ⇒ every
     * entrant; off ⇒ every rewarded rank. Snapshotted onto every occurrence at
     * creation, like `rakeBps`. Absent/0 ⇒ no tickets. Only meaningful on paid
     * competitions (positive `entryFees`) — see the refinement.
     */
    autopilotPriorityTickets: z.number().int().min(0).optional(),
  })
  .superRefine((template, ctx) => {
    // Opt-in locks `entryFees` from the user's balance, so it needs an amount; "0" is a free competition.
    if (template.userCanOptIn && template.entryFees == null) {
      ctx.addIssue({
        code: "custom",
        path: ["entryFees"],
        message: 'entryFees is required when userCanOptIn is true ("0" for a free competition)',
      });
    }
    if (isFreeArenaCompetition(template)) assertFreeCompetitionInvariants(template, ctx);
    if (template.isSpinAndGo && template.rule.data.slots.some((slot) => slot.tickets != null)) {
      ctx.addIssue({ code: "custom", path: ["rule", "data", "slots"], message: SPIN_AND_GO_USDT_ONLY_PRIZES_MESSAGE });
    }
    // Tickets reward a paid competition — a free one granting them would mint
    // priority out of thin air.
    if (
      template.autopilotPriorityTickets != null &&
      template.autopilotPriorityTickets > 0 &&
      !(template.entryFees != null && Number(template.entryFees) > 0)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["autopilotPriorityTickets"],
        message: "autopilotPriorityTickets requires a positive entryFees (paid competitions only)",
      });
    }
    // cedeFillPrizePool guarantees the configured pool, so it must be positive.
    assertCedeFillPrizePoolHasPositivePool(template, ctx);
    // Spin & Go needs a positive fee to multiply and can't stack with cede-fill.
    assertSpinAndGoInvariants(template, ctx);
    if (template.battleRoyale != null) {
      assertBattleRoyaleInvariants(
        { ...template, minVolumeUSD: template.rule.data.minVolumeUSD },
        {
          drawdown: ["drawdown"],
          isSpinAndGo: ["isSpinAndGo"],
          userCanOptIn: ["userCanOptIn"],
          maxParticipants: ["maxParticipants"],
          minVolumeUSD: ["rule", "data", "minVolumeUSD"],
        },
        ctx,
      );
    }
    if (isRisingDrawdown(template.drawdown) && template.marketType !== CampaignMarketType.PERPS) {
      ctx.addIssue({ code: "custom", path: ["drawdown"], message: "rising drawdown requires a perps competition" });
    }
  })
  // A Battle Royale is a perps competition; templates stored without a market type read as one.
  .transform((template) =>
    template.battleRoyale != null && template.marketType == null
      ? { ...template, marketType: CampaignMarketType.PERPS }
      : template,
  );
export type CedeArenaCompetitionBlueprintTemplate = z.infer<typeof CedeArenaCompetitionBlueprintTemplateSchema>;

export const CedeArenaCompetitionBlueprintDtoSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, "Name is required"),
    /** Inclusive UTC instant from which occurrences may be generated. */
    validityStart: z.coerce.date(),
    /** Exclusive UTC instant after which no occurrences are generated. */
    validityEnd: z.coerce.date(),
    /** Length of each competition window, in seconds. */
    competitionDurationSeconds: z.number().int().positive(),
    exchangeIds: z.array(z.string()).min(1, "At least one exchange is required"),
    pairSymbols: z.array(tokenPairSymbolSchema).min(1, "At least one pair symbol is required"),
    recurrenceRule: RecurrenceRuleSchema,
    template: CedeArenaCompetitionBlueprintTemplateSchema,
  })
  .refine(
    ({ template, competitionDurationSeconds }) =>
      !isRisingDrawdown(template.drawdown) ||
      isRisingDrawdownIntervalWithinDuration(template.drawdown.stepIntervalSeconds, competitionDurationSeconds),
    {
      message: RISING_DRAWDOWN_INTERVAL_EXCEEDS_DURATION_MESSAGE,
      path: ["template", "drawdown", "stepIntervalSeconds"],
    },
  );
export type CedeArenaCompetitionBlueprintDto = z.infer<typeof CedeArenaCompetitionBlueprintDtoSchema>;

/** Body for `POST /v1/admin/cede-arena/blueprints`. */
export const SaveCedeArenaCompetitionBlueprintDtoSchema = CedeArenaCompetitionBlueprintDtoSchema.superRefine(
  (blueprint, ctx) => {
    // Eliminations run on the paper engine; elsewhere the schedule would be dropped.
    if (
      blueprint.template.battleRoyale != null &&
      !blueprint.exchangeIds.every((id) => id === SUPPORTED_EXCHANGES.PAPER)
    ) {
      ctx.addIssue({ code: "custom", path: ["exchangeIds"], message: "Battle Royale runs on the paper exchange only" });
    }
    // Eliminations rank through the perps PnL rule, which generation derives from `marketType` (absent reads as perps).
    if (blueprint.template.battleRoyale != null && blueprint.template.marketType !== CampaignMarketType.PERPS) {
      ctx.addIssue({
        code: "custom",
        path: ["template", "marketType"],
        message: "Battle Royale requires a perps competition",
      });
    }
    // Save-only: stored blueprints still hydrate.
    if (
      blueprint.template.battleRoyale != null &&
      blueprint.template.maxParticipants != null &&
      blueprint.template.maxParticipants > BATTLE_ROYALE_MAX_PLAYERS
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["template", "maxParticipants"],
        message: `A Battle Royale takes at most ${BATTLE_ROYALE_MAX_PLAYERS} players`,
      });
    }
    // Paper accounts are funded with it whatever Competition Mode says; 0 fails every occurrence.
    const { initialDeposit } = blueprint.template.rule.data;
    if (blueprint.exchangeIds.includes(SUPPORTED_EXCHANGES.PAPER) && (initialDeposit == null || initialDeposit <= 0)) {
      ctx.addIssue({
        code: "custom",
        path: ["template", "rule", "data", "initialDeposit"],
        message: "A paper competition needs a positive Initial Deposit (paper accounts are seeded with it)",
      });
    }
    // Save-only, like the checks above, so a stored zero-padded or unordered split still hydrates.
    const { slots, isCompetition } = blueprint.template.rule.data;
    const slotsPath = ["template", "rule", "data", "slots"];
    if (slots.some(isEmptyPnlRewardSlot)) {
      ctx.addIssue({ code: "custom", path: slotsPath, message: EMPTY_PRIZE_SLOT_MESSAGE });
    }
    // The competition-mode refinement already orders the slots.
    if (!isCompetition) assertSlotsUsdtNonIncreasing(slots, ctx, slotsPath);
    // A paid pot needs a USDT place to land on; ticket-only slots would leave it unpayable at start.
    if (
      !isFreeArenaCompetition(blueprint.template) &&
      slots.length > 0 &&
      slots.every((slot) => slot.reward.isZero())
    ) {
      ctx.addIssue({ code: "custom", path: slotsPath, message: PAID_COMPETITION_NEEDS_USDT_PRIZE_MESSAGE });
    }
  },
);
export type SaveCedeArenaCompetitionBlueprintDto = z.infer<typeof SaveCedeArenaCompetitionBlueprintDtoSchema>;

/** One row in the blueprint list. */
export const CedeArenaCompetitionBlueprintPreviewDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  validityStart: z.coerce.date(),
  validityEnd: z.coerce.date(),
  competitionDurationSeconds: z.number().int().positive(),
  exchangeIds: z.array(z.string()),
  pairSymbols: z.array(tokenPairSymbolSchema),
  recurrenceRule: RecurrenceRuleSchema,
  /** The template's elimination schedule; absent or null ⇒ classic (absent from an API older than the CMS). */
  battleRoyale: BattleRoyaleConfigReadSchema.nullish(),
  /** Number of `TradeToEarnCampaign` rows generated from this blueprint. */
  competitionsCount: z.number().int().nonnegative(),
});
export type CedeArenaCompetitionBlueprintPreviewDto = z.infer<typeof CedeArenaCompetitionBlueprintPreviewDtoSchema>;

/** Response payload for `GET /v1/admin/cede-arena/blueprints/previews`. */
export const ListCedeArenaCompetitionBlueprintsResponseSchema = z.object({
  blueprints: CedeArenaCompetitionBlueprintPreviewDtoSchema.array(),
});
export type ListCedeArenaCompetitionBlueprintsResponse = z.infer<
  typeof ListCedeArenaCompetitionBlueprintsResponseSchema
>;

/** Response payload for `GET /v1/admin/cede-arena/blueprints/:id` — config + its competitions. */
export const GetCedeArenaCompetitionBlueprintResponseSchema = z.object({
  blueprint: CedeArenaCompetitionBlueprintDtoSchema,
  competitions: TradeToEarnCampaignPreviewDtoSchema.array(),
});
export type GetCedeArenaCompetitionBlueprintResponse = z.infer<typeof GetCedeArenaCompetitionBlueprintResponseSchema>;

/** Response payload for `GET /v1/admin/cede-arena/competitions/unlinked`. */
export const ListUnlinkedArenaCompetitionsResponseSchema = z.object({
  competitions: TradeToEarnCampaignPreviewDtoSchema.array(),
});
export type ListUnlinkedArenaCompetitionsResponse = z.infer<typeof ListUnlinkedArenaCompetitionsResponseSchema>;

/**
 * Query params for `GET /v1/admin/cede-arena/competitions` — all arena
 * competitions whose window overlaps the `[from, to]` range (one month at a
 * time, to keep the calendar payload bounded).
 */
export const ListArenaCompetitionsInRangeQuerySchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date(),
});
export type ListArenaCompetitionsInRangeQueryDto = z.infer<typeof ListArenaCompetitionsInRangeQuerySchema>;

/** Response payload for `GET /v1/admin/cede-arena/competitions`. */
export const ListArenaCompetitionsResponseSchema = z.object({
  competitions: TradeToEarnCampaignPreviewDtoSchema.array(),
});
export type ListArenaCompetitionsResponse = z.infer<typeof ListArenaCompetitionsResponseSchema>;
